import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import type { MeResponse, PreferencesResponse, UserPreferences, UserPreferencesUpdate, UserProfileResponse } from "@starsach/shared-types";
import { fetchJson, setSessionRenewer } from "../api/client";
import { isMock, loadConfig, type AppConfig } from "../config";
import { applyAppearance, cachedTheme, DEFAULT_PREFERENCES } from "../lib/preferences";
import { cognitoAuth, mockAuth, type AuthAdapter, type FreshTokens, type Session, type SignInRoute } from "./auth";
import { clearPendingSignOut, pendingSignOut, rememberPendingSignOut } from "./pending-sign-out";
import { ApiRequestError } from "../api/client";
import type { SignedIn } from "./sign-in-machine";

interface AuthState {
  status: "loading" | "signed-out" | "signed-in";
  config?: AppConfig;
  session?: Session;
  me?: MeResponse;
  /** Section 13.1: the caller's own, defaults until /v1/me answers. */
  preferences: UserPreferences;
  /** Leaves for the hosted endpoint (federation, or the interim first sign-in and reset); `returnTo` is where the callback lands. */
  signIn: (returnTo: string | undefined, route: SignInRoute) => Promise<void>;
  /** A sign-in on Starsach's own screen finished: its tokens and confirmed device become this browser's session. */
  acceptSignIn: (result: SignedIn) => Promise<boolean>;
  /**
   * Ends this sign-in: records it at the API first (every token issued until now is refused there), then revokes the
   * refresh token and leaves through Cognito's logout. `alreadyRecorded` when the caller recorded it before a global sign-out.
   */
  signOut: (opts?: { alreadyRecorded?: boolean }) => Promise<void>;
  /**
   * Tells the API every token issued until now is finished with (POST /v1/me/sign-out). Waits up to 30 s; past that,
   * or on a failure, the sign-out goes ahead and this browser finishes the stamp at the person's next sign-in (#621).
   */
  recordSignOut: () => Promise<void>;
  /** A sign-out is waiting on the API (up to 30 s): the layout says so rather than look frozen. */
  signingOut: boolean;
  /**
   * The access token as it is now, renewed first when under a minute is left or when `renew` is asked (gairm/lms#430):
   * a security change uses this at the moment of the call, never a token captured when the page rendered, which a
   * step-up or a renewal in the meantime has replaced.
   */
  accessToken: (renew?: boolean) => Promise<string | undefined>;
  completeSignIn: () => Promise<string>;
  /** Saves a patch on the server and applies it at once. */
  updatePreferences: (patch: UserPreferencesUpdate) => Promise<void>;
  /** Section 13.2: the display name; empty clears it. */
  updateProfile: (firstName: string, lastName: string) => Promise<void>;
  /** Section 13.3: after a step-up, the fresh tokens replace the session's. */
  replaceTokens: (tokens: FreshTokens) => Promise<void>;
  /** Re-reads /v1/me, e.g. after the token's claims changed. */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig>();
  const [adapter, setAdapter] = useState<AuthAdapter>();
  // The adapter arrives after config.json is fetched, but the callback page
  // (and an eager click on Sign in) can ask for it before then. Anything
  // that needs the adapter waits for it rather than quietly doing nothing:
  // on the callback route "nothing" meant redirecting to / with the code
  // still in the URL and the sign-in never completing.
  const adapterReady = useRef<{ promise: Promise<AuthAdapter>; resolve: (a: AuthAdapter) => void }>();
  if (!adapterReady.current) {
    let resolve!: (a: AuthAdapter) => void;
    const promise = new Promise<AuthAdapter>((r) => (resolve = r));
    adapterReady.current = { promise, resolve };
  }
  const [session, setSession] = useState<Session | null>();
  const [me, setMe] = useState<MeResponse>();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    (async () => {
      const cfg = await loadConfig();
      const a = isMock ? mockAuth() : cognitoAuth(cfg);
      const s = await a.getSession();
      if (cancelled) return;
      setConfig(cfg);
      setAdapter(a);
      adapterReady.current!.resolve(a);
      setSession(s);
      // A renewal, a step-up, or another tab's sign-in or sign-out replaces the session every request reads.
      unsubscribe = a.onSessionChange((next) => {
        setSession(next);
        if (!next) setMe(undefined);
      });
      let inFlight: Promise<Session | null> | undefined;
      setSessionRenewer(() => (inFlight ??= a.renew().finally(() => (inFlight = undefined))));
      if (s) {
        // /v1/me is the server's view of the role: the UI never derives it from the token itself.
        setMe(await fetchJson<MeResponse>(cfg, s, "/v1/me"));
      }
    })().catch(() => setSession(null));
    return () => {
      cancelled = true;
      unsubscribe?.();
      setSessionRenewer(undefined);
    };
  }, []);

  // R3: renewed a minute before it lapses, whether or not the page is asking for anything.
  useEffect(() => {
    if (!adapter || !session) return;
    const timer = setTimeout(() => void adapter.renew().then(setSession), Math.max(0, session.expiresAt - Date.now() - 60_000));
    return () => clearTimeout(timer);
  }, [adapter, session]);

  // A tab left in the background past the token's life renews before the first click, not after it fails.
  useEffect(() => {
    if (!adapter || !session) return;
    const check = () => {
      if (document.visibilityState === "visible" && session.expiresAt - Date.now() < 60_000) void adapter.renew().then(setSession);
    };
    document.addEventListener("visibilitychange", check);
    return () => document.removeEventListener("visibilitychange", check);
  }, [adapter, session]);

  const signIn = useCallback(
    async (returnTo: string | undefined, route: SignInRoute) => (await adapterReady.current!.promise).signIn(returnTo ?? location.pathname + location.search, route),
    [location],
  );
  const acceptSignIn = useCallback(async (result: SignedIn) => Boolean(await (await adapterReady.current!.promise).acceptSignIn(result)), []);
  const accessToken = useCallback(
    async (renew = false) => {
      if (!adapter) return undefined;
      const now = await adapter.getSession();
      const s = renew || !now || now.expiresAt - Date.now() < 60_000 ? await adapter.renew() : now;
      return s?.accessToken;
    },
    [adapter],
  );
  const [signingOut, setSigningOut] = useState(false);
  const recordSignOut = useCallback(async () => {
    if (!config || !session) return;
    const at = new Date().toISOString();
    setSigningOut(true);
    try {
      // Chris, 2 Oct (#621): wait up to 30 seconds, then sign this browser out anyway and say so.
      await fetchJson(config, session, "/v1/me/sign-out", { method: "POST", signal: AbortSignal.timeout(30_000) });
    } catch {
      if (me) rememberPendingSignOut(me.subject, at);
    }
  }, [config, session, me]);
  // The adapter runs R4's order itself: stamp (unless already recorded; up to 30 s, kept for the next sign-in if it
  // fails, #621), forget the device, revoke, clear, leave.
  const signOut = useCallback(
    async (opts?: { alreadyRecorded?: boolean }) => {
      setSigningOut(true);
      await adapter?.signOut(opts);
    },
    [adapter],
  );
  // #621: the stamp a sign-out here could not record, finished now this person is signed in again - with its
  // original time, which the API takes as long as it is earlier than this session. A refusal (400) cannot succeed later.
  useEffect(() => {
    if (!config || !session || !me) return;
    const at = pendingSignOut(me.subject);
    if (!at) return;
    void fetchJson(config, session, "/v1/me/sign-out", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ at }) }).then(
      () => clearPendingSignOut(me.subject),
      (e: unknown) => {
        if (e instanceof ApiRequestError && e.status === 400) clearPendingSignOut(me.subject);
      },
    );
  }, [config, session, me]);
  const completeSignIn = useCallback(async () => (await adapterReady.current!.promise).completeSignIn(), []);

  const preferences = useMemo(() => me?.preferences ?? { ...DEFAULT_PREFERENCES, theme: cachedTheme() }, [me?.preferences]);
  // Theme and motion are attributes on <html>; the CSS does the rest. Re-applied when the preference changes, and following the OS while "system"/"auto".
  useEffect(() => applyAppearance(preferences), [preferences]);
  const updatePreferences = useCallback(
    async (patch: UserPreferencesUpdate) => {
      if (!config || !session) throw new Error("Not signed in");
      const r = await fetchJson<PreferencesResponse>(config, session, "/v1/me/preferences", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(patch) });
      setMe((m) => (m ? { ...m, preferences: r.preferences } : m));
    },
    [config, session],
  );

  const updateProfile = useCallback(
    async (firstName: string, lastName: string) => {
      if (!config || !session) throw new Error("Not signed in");
      const r = await fetchJson<UserProfileResponse>(config, session, "/v1/me/profile", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ firstName, lastName }) });
      setMe((m) => {
        if (!m) return m;
        const { displayName: _d, firstName: _f, lastName: _l, ...rest } = m;
        return { ...rest, ...(r.firstName ? { firstName: r.firstName } : {}), ...(r.lastName ? { lastName: r.lastName } : {}), ...(r.displayName ? { displayName: r.displayName } : {}) };
      });
    },
    [config, session],
  );

  const replaceTokens = useCallback(
    async (tokens: FreshTokens) => {
      const s = await (await adapterReady.current!.promise).replaceTokens(tokens);
      if (s) setSession(s);
    },
    [],
  );
  const refreshMe = useCallback(async () => {
    if (config && session) setMe(await fetchJson<MeResponse>(config, session, "/v1/me"));
  }, [config, session]);

  const value = useMemo<AuthState>(
    () => ({
      status: session === undefined ? "loading" : session === null ? "signed-out" : me ? "signed-in" : "loading",
      config,
      session: session ?? undefined,
      me,
      preferences,
      signIn,
      acceptSignIn,
      signOut,
      recordSignOut,
      signingOut,
      accessToken,
      completeSignIn,
      updatePreferences,
      updateProfile,
      replaceTokens,
      refreshMe,
    }),
    [config, session, me, preferences, signIn, acceptSignIn, signOut, recordSignOut, signingOut, accessToken, completeSignIn, updatePreferences, updateProfile, replaceTokens, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
